import { useCallback, useEffect, useMemo, useState } from 'react';

import { bridgeAtom, renderedColorsAtom, useSetValue, useValue } from '@/store';

import { colorsMap } from '@/modules/common';

import { ColorItem } from './ColorItem';
import { ColorsWrapper } from './Colors.styles';
import { ColorSource } from './colorSource';

interface ColorsProps {
  handle: string;
  value?: string;
  index: number;
  values: string[];
  selectOption: (value: string, index: number) => void;
}

type ColorItemProps = {
  id: string;
  colorName: string;
  source: ColorSource;
  imageUrl?: string;
  color?: string;
};

const getValidCssColor = (colorName: string) => {
  const s = new Option().style;
  s.color = colorName.toLowerCase();
  return s.color !== '' ? s.color : null;
};

const getColorImageUrl = (id: string) => `https://pickleball.co.uk/cdn/shop/files/${id}_50x50.png`;

const checkImageExists = (url: string) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = url;
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
  });
};

export const Colors = ({ handle, index, value, values, selectOption }: ColorsProps) => {
  const setRenderedColors = useSetValue(renderedColorsAtom);

  const { productCard } = useValue(bridgeAtom);

  const [colorsList, setColorsList] = useState<ColorItemProps[]>([]);

  const getColorData = useCallback(
    async (
      colorId: string
    ): Promise<{
      source: ColorSource;
      color?: string;
      imageUrl?: string;
    }> => {
      const mappedColor = productCard?.variantsConfig?.color?.colorsMap?.[colorId] || colorsMap?.[colorId];
      const hexColor = getValidCssColor(colorId);

      if (hexColor) {
        return { color: hexColor, source: 'css' };
      }

      const url = getColorImageUrl(colorId);
      const isExists = await checkImageExists(url);
      if (isExists) {
        return { imageUrl: url, source: 'image' };
      }

      if (mappedColor) {
        return { color: mappedColor, source: 'css' };
      }

      return { source: 'fallback' };
    },
    [productCard]
  );

  const onClick = useCallback(
    (newValue: string) => {
      selectOption?.(newValue, index);
    },
    [index, selectOption]
  );

  const getColorsList = useCallback(async (): Promise<ColorItemProps[]> => {
    const results = await Promise.all(
      values.map(async (colorName) => {
        const id = colorName.toLowerCase().replace(/[\s/]+/g, '-');
        const colorData = await getColorData(id);
        return { ...colorData, colorName, id };
      })
    );
    return results;
  }, [values, getColorData]);

  useEffect(() => {
    let isReady = true;
    (async () => {
      const resolved = await getColorsList();
      if (!isReady) {
        return;
      }

      setColorsList(resolved);
    })();

    return () => {
      isReady = false;
    };
  }, [getColorsList]);

  useEffect(() => {
    setRenderedColors((prev) => {
      const newValue = Boolean(colorsList.length);
      if (prev[handle] === newValue) {
        return prev;
      }
      return {
        ...prev,
        [handle]: newValue,
      };
    });
  }, [colorsList, handle, setRenderedColors]);

  const isFallbacks = useMemo(() => colorsList.some(({ source }) => source === 'fallback'), [colorsList]);

  return (
    <ColorsWrapper>
      {colorsList.map(({ colorName, source, imageUrl, color }) => (
        <ColorItem
          key={colorName}
          active={colorName === value}
          colorName={colorName}
          color={color}
          imageUrl={imageUrl}
          source={isFallbacks ? 'fallback' : source}
          onClick={() => onClick(colorName)}
        />
      ))}
    </ColorsWrapper>
  );
};
